import { useState, useEffect } from 'react'
import { GroupService, type GroupWithMembers } from '../../services/groups'
import type { User } from '../../services/users'
import { formatNumber } from '../../utils/formatNumber'

interface GroupManagementDialogProps {
  isOpen: boolean
  onClose: () => void
  schoolId: string
  group?: GroupWithMembers | null
  onSave: () => void
}

const GroupManagementDialog = ({
  isOpen,
  onClose,
  schoolId,
  group,
  onSave
}: GroupManagementDialogProps) => {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [isEntreClub, setIsEntreClub] = useState(false)
  const [availableStudents, setAvailableStudents] = useState<User[]>([])
  const [selectedStudentIds, setSelectedStudentIds] = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')

  // Load students when dialog opens
  useEffect(() => {
    if (!isOpen || !schoolId) return

    const loadData = async () => {
      try {
        setIsLoading(true)
        setError('')

        // Load all students (don't filter by group - we need all students to allow deselection)
        const students = await GroupService.getAvailableStudents(schoolId)
        setAvailableStudents(students)

        // If editing existing group, set form values and selected students
        if (group) {
          setName(group.name)
          setDescription(group.description || '')
          setIsEntreClub(group.is_entre_club ?? false)
          const memberIds = new Set(group.members.map(m => m.student_id))
          setSelectedStudentIds(memberIds)
        } else {
          setName('')
          setDescription('')
          setIsEntreClub(false)
          setSelectedStudentIds(new Set())
        }
      } catch (err) {
        console.error('Failed to load students:', err)
        setError('生徒データの読み込みに失敗しました')
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [isOpen, schoolId, group])

  const handleToggleStudent = (studentId: string) => {
    const newSelected = new Set(selectedStudentIds)
    if (newSelected.has(studentId)) {
      newSelected.delete(studentId)
    } else {
      newSelected.add(studentId)
    }
    setSelectedStudentIds(newSelected)
  }

  const handleSave = async () => {
    if (!name.trim()) {
      setError('グループ名を入力してください')
      return
    }

    try {
      setIsSaving(true)
      setError('')

      if (group) {
        // Update existing group
        await GroupService.updateGroup(group.id, {
          name: name.trim(),
          description: description.trim() || undefined,
          isEntreClub
        })

        // Get current members
        const currentMemberIds = new Set(group.members.map(m => m.student_id))

        // Find students to add and remove
        const studentsToAdd = Array.from(selectedStudentIds).filter(id => !currentMemberIds.has(id))
        const studentsToRemove = Array.from(currentMemberIds).filter(id => !selectedStudentIds.has(id))

        // Add new students
        if (studentsToAdd.length > 0) {
          await GroupService.addStudentsToGroup(group.id, studentsToAdd)
        }

        // Remove students
        for (const studentId of studentsToRemove) {
          await GroupService.removeStudentFromGroup(group.id, studentId)
        }
      } else {
        // Create new group
        const newGroup = await GroupService.createGroup({
          schoolId,
          name: name.trim(),
          description: description.trim() || undefined,
          isEntreClub
        })

        // Add students to the new group
        if (selectedStudentIds.size > 0) {
          await GroupService.addStudentsToGroup(newGroup.id, Array.from(selectedStudentIds))
        }
      }

      onSave()
      onClose()
    } catch (err: any) {
      console.error('Failed to save group:', err)
      if (err.message?.includes('unique')) {
        setError('このグループ名は既に使用されています')
      } else {
        setError('グループの保存に失敗しました')
      }
    } finally {
      setIsSaving(false)
    }
  }

  // Filter students based on search query
  const filteredStudents = availableStudents.filter(student => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      student.name.toLowerCase().includes(query) ||
      student.grade?.toLowerCase().includes(query) ||
      student.class?.toLowerCase().includes(query) ||
      student.attendance_number?.toLowerCase().includes(query)
    )
  })

  // Separate selected and unselected students
  const selectedStudents = filteredStudents.filter(s => selectedStudentIds.has(s.id))
  const unselectedStudents = filteredStudents.filter(s => !selectedStudentIds.has(s.id))

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">
            {group ? 'グループを編集' : '新しいグループを作成'}
          </h2>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Group Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              グループ名 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="例: プロジェクトA"
              maxLength={100}
            />
          </div>

          {/* Group Description */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              説明（任意）
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="グループの目的や詳細を入力してください"
              rows={3}
              maxLength={500}
            />
          </div>

          {/* Entre-club flag */}
          <div className="mb-6 flex items-center gap-2">
            <input
              type="checkbox"
              id="is_entre_club"
              checked={isEntreClub}
              onChange={e => setIsEntreClub(e.target.checked)}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
            />
            <label htmlFor="is_entre_club" className="text-sm font-medium text-gray-700">
              アントレ部グループとして設定する
            </label>
          </div>

          {/* Student Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              生徒を選択（{formatNumber(selectedStudentIds.size)}名選択中）
            </label>

            {/* Search */}
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 mb-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="名前、学年、クラス、出席番号で検索..."
            />

            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
              </div>
            ) : (
              <div className="border border-gray-300 rounded-md max-h-80 overflow-y-auto">
                {/* Selected Students */}
                {selectedStudents.length > 0 && (
                  <div>
                    <div className="px-3 py-2 bg-purple-50 border-b border-gray-200 sticky top-0">
                      <p className="text-xs font-medium text-purple-700">選択済み</p>
                    </div>
                    {selectedStudents.map(student => (
                      <label
                        key={student.id}
                        className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                      >
                        <input
                          type="checkbox"
                          checked={true}
                          onChange={() => handleToggleStudent(student.id)}
                          className="mr-3 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{student.name}</p>
                          <p className="text-xs text-gray-500">
                            {student.grade && student.class ? `${student.grade} ${student.class}` : '学年・クラス未設定'}
                            {student.attendance_number && ` ・ No.${student.attendance_number}`}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                )}

                {/* Unselected Students */}
                {unselectedStudents.length > 0 && (
                  <div>
                    {selectedStudents.length > 0 && (
                      <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 sticky top-0">
                        <p className="text-xs font-medium text-gray-700">未選択</p>
                      </div>
                    )}
                    {unselectedStudents.map(student => (
                      <label
                        key={student.id}
                        className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                      >
                        <input
                          type="checkbox"
                          checked={false}
                          onChange={() => handleToggleStudent(student.id)}
                          className="mr-3 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{student.name}</p>
                          <p className="text-xs text-gray-500">
                            {student.grade && student.class ? `${student.grade} ${student.class}` : '学年・クラス未設定'}
                            {student.attendance_number && ` ・ No.${student.attendance_number}`}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                )}

                {filteredStudents.length === 0 && (
                  <div className="px-3 py-8 text-center text-gray-500 text-sm">
                    {searchQuery ? '検索結果が見つかりません' : '生徒が見つかりません'}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            キャンセル
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !name.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default GroupManagementDialog
